import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff, Mail, Lock, LogIn } from 'lucide-react';
import api from '../lib/api';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, completeTwoFa, error, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justRegistered = searchParams.get('registered') === '1';
  const sessionExpired = searchParams.get('reason') === 'session_expired';

  const [localError, setLocalError] = React.useState(
    sessionExpired ? 'פג תוקף החיבור — אנא התחבר מחדש' : ''
  );

  // Handle Google OAuth callback token
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleToken = params.get('google_token');
    const googleUser = params.get('google_user');
    const errorParam = params.get('error');
    if (googleToken && googleUser) {
      try {
        localStorage.setItem('token', googleToken);
        localStorage.setItem('user', decodeURIComponent(googleUser));
        window.location.href = '/';
      } catch (e) { setLocalError('שגיאה בכניסה עם Google'); }
    }
    if (errorParam) {
      const msgs: Record<string, string> = {
        google_denied: 'הכניסה עם Google בוטלה',
        google_token: 'שגיאה בחיבור ל-Google',
        google_no_email: 'לא ניתן לקבל אימייל מ-Google',
        account_disabled: 'החשבון לא פעיל',
        google_failed: 'שגיאה בכניסה עם Google',
        google_not_registered: 'המשתמש לא קיים במערכת. פנה למנהל לקבלת גישה.',
      };
      setLocalError(msgs[errorParam] || 'שגיאה בכניסה עם Google');
    }
  }, []);

  const [otpMode, setOtpMode] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  // 2FA חובה למנהלים: הסיסמה אומתה, קוד נשלח למייל, וממתינים להזנתו.
  // preAuthToken הוא הוכחת שלב-הסיסמה שהשרת החזיר מ-/auth/login.
  const [twoFaToken, setTwoFaToken] = useState<string | null>(null);
  const [twoFaCode, setTwoFaCode] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    if (!email.trim()) {
      setLocalError('יש להזין כתובת אימייל');
      return;
    }
    if (!password && !otpMode) {
      setLocalError('יש להזין סיסמה');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setLocalError('כתובת אימייל לא תקינה');
      return;
    }

    if (otpMode) {
      if (!otpSent) {
        setOtpLoading(true);
        try {
          await api.post('/auth/request-otp', { email });
          setOtpSent(true);
        } catch (err: any) {
          setLocalError(err?.response?.data?.error || 'שגיאה בשליחת הקוד');
        } finally {
          setOtpLoading(false);
        }
        return;
      }

      if (!otpCode.trim()) {
        setLocalError('יש להזין קוד חד-פעמי');
        return;
      }
      setOtpLoading(true);
      try {
        const res = await api.post('/auth/login-otp', { email, code: otpCode });
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        if (res.data.user?.mustChangePassword) {
          navigate('/change-password-required');
        } else {
          navigate('/');
        }
      } catch (err: any) {
        setLocalError(err?.response?.data?.error || 'שגיאה בכניסה עם קוד');
      } finally {
        setOtpLoading(false);
      }
      return;
    }

    // שלב שני של 2FA למנהלים — הזנת הקוד שנשלח למייל
    if (twoFaToken) {
      if (!twoFaCode.trim()) {
        setLocalError('יש להזין את הקוד שנשלח למייל');
        return;
      }
      try {
        const userData = await completeTwoFa(twoFaToken, twoFaCode.trim());
        if ((userData as any)?.mustChangePassword) {
          navigate('/change-password-required');
        } else {
          navigate('/');
        }
      } catch (err) {
        console.error(err);
      }
      return;
    }

    if (!email.trim() || !password.trim()) {
      setLocalError('יש למלא אימייל וסיסמה');
      return;
    }
    try {
      const userData = await login(email, password);
      if ((userData as any)?.otpRequired) {
        setTwoFaToken((userData as any).preAuthToken);
        setTwoFaCode('');
        return;
      }
      if ((userData as any)?.mustChangePassword) {
        navigate('/change-password-required');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div
      dir="rtl"
      className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden bg-gradient-to-br from-[#1565C0] via-[#311B92] to-[#6200EA]"
    >
      {/* Decorative blobs (CSS only, no images) */}
      <div className="absolute top-[-15%] right-[-10%] w-[40vw] h-[40vw] max-w-[480px] max-h-[480px] rounded-full bg-white/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[50vw] h-[50vw] max-w-[600px] max-h-[600px] rounded-full bg-violet-400/10 blur-3xl pointer-events-none" />
      <div className="absolute top-[30%] left-[15%] w-[20vw] h-[20vw] max-w-[240px] max-h-[240px] rounded-full bg-blue-300/10 blur-2xl pointer-events-none" />

      {/* Card */}
      <div className="relative z-10 w-full max-w-md bg-white dark:bg-[#1E1E1E] rounded-3xl shadow-md-4 overflow-hidden">

        {/* Card top gradient strip */}
        <div className="h-1.5 w-full bg-gradient-to-l from-[#6200EA] to-[#1565C0]" />

        <div className="p-5 md:p-8">
          {/* Logo */}
          <div className="text-center mb-3">
            <img src="/amutech-logo.png" alt="Amutech" className="w-[160px] h-[160px] md:w-[220px] md:h-[220px] object-contain mx-auto" />
          </div>

          {/* Success banner after registration */}
          {justRegistered && (
            <div className="flex items-start gap-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/50 text-green-700 dark:text-green-400 px-4 py-3 rounded-xl text-sm mb-3">
              <span className="mt-0.5">✅</span>
              <span>נרשמת בהצלחה! כנס עם הפרטים שלך</span>
            </div>
          )}

          {/* Title */}
          <div className="text-center mb-3">
            <h2 className="text-xl font-bold text-[#1C1B1F] dark:text-[#E6E1E5]">כניסה למערכת</h2>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">

            <div className="relative">
              <label htmlFor="login-email" className="block text-xs font-semibold text-[#49454F] dark:text-[#CAC4D0] mb-1.5 mr-1">
                כתובת אימייל
              </label>
              <div className="relative">
                <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#BDBDBD] pointer-events-none" />
                <input
                  id="login-email"
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="md-input"
                  style={{ direction: 'ltr', textAlign: 'left', paddingLeft: '2.75rem', paddingRight: '1rem' }}
                  required
                />
              </div>
            </div>

            {twoFaToken ? (
              <div className="relative">
                <label htmlFor="login-2fa" className="block text-xs font-semibold text-[#49454F] dark:text-[#CAC4D0] mb-1.5 mr-1">
                  קוד אימות (נשלח למייל)
                </label>
                <input
                  id="login-2fa"
                  type="text"
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value)}
                  placeholder="הזן 6 ספרות"
                  className="md-input"
                  style={{ direction: 'ltr', textAlign: 'center', letterSpacing: '0.3em' }}
                  maxLength={6}
                  autoFocus
                />
                <p className="text-xs text-[#79747E] dark:text-[#CAC4D0] mt-1.5 mr-1">
                  שלחנו קוד חד-פעמי לכתובת המייל שלך. הקוד תקף ל-10 דקות.
                </p>
              </div>
            ) : !otpMode ? (
              <div className="relative">
                <label htmlFor="login-password" className="block text-xs font-semibold text-[#49454F] dark:text-[#CAC4D0] mb-1.5 mr-1">
                  סיסמה
                </label>
                <div className="relative">
                  <Lock size={17} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#BDBDBD] pointer-events-none" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="md-input pr-11 pl-11"
                    style={{ direction: 'ltr', textAlign: 'right', paddingRight: '2.75rem', paddingLeft: '2.75rem' }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#BDBDBD] hover:text-[#1565C0] transition-colors duration-200"
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>
            ) : otpSent ? (
              <div className="relative">
                <label htmlFor="login-otp" className="block text-xs font-semibold text-[#49454F] dark:text-[#CAC4D0] mb-1.5 mr-1">
                  קוד חד-פעמי
                </label>
                <input
                  id="login-otp"
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder="הזן 6 ספרות"
                  className="md-input"
                  style={{ direction: 'ltr', textAlign: 'center', letterSpacing: '0.3em' }}
                  maxLength={6}
                />
              </div>
            ) : null}

            {/* Error */}
            {(localError || error) && (
              <div className="flex items-start gap-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 text-[#B00020] dark:text-[#CF6679] px-4 py-3 rounded-xl text-sm">
                <span className="mt-0.5">⚠️</span>
                <span>{localError || error}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || otpLoading}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-full bg-gradient-to-l from-[#6200EA] to-[#1565C0] text-white font-semibold text-base shadow-md-2 hover:shadow-md-3 hover:opacity-95 active:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {(loading || otpLoading) ? (
                <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
              ) : (
                <>
                  <LogIn size={18} />
                  {twoFaToken ? 'אימות וכניסה' : otpMode ? (otpSent ? 'כניסה עם קוד' : 'שלח קוד חד-פעמי') : 'כניסה'}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => { window.location.href = '/api/auth/google/start'; }}
              className="w-full flex items-center justify-center gap-2.5 py-3 px-6 rounded-full bg-white border border-[#E0E0E0] text-[#1C1B1F] font-semibold text-base hover:bg-[#F8F8F8] hover:shadow-sm transition-all duration-200 mt-3"
            >
              <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              התחברות עם Google
            </button>
          </form>

          {/* OTP / Forgot password */}
          <div className="text-center mt-3 space-y-2">
            {twoFaToken ? (
              <button
                type="button"
                onClick={() => { setTwoFaToken(null); setTwoFaCode(''); setLocalError(''); }}
                className="text-sm text-[#1565C0] hover:underline font-medium block w-full"
              >
                חזרה לכניסה עם סיסמה
              </button>
            ) : (
              <button
                type="button"
                onClick={() => { setOtpMode(v => !v); setOtpSent(false); setOtpCode(''); setLocalError(''); }}
                className="text-sm text-[#1565C0] hover:underline font-medium block w-full"
              >
                {otpMode ? 'חזרה לכניסה עם סיסמה' : 'התחברות עם קוד חד-פעמי'}
              </button>
            )}
            {!otpMode && (
              <Link to="/forgot-password" className="text-sm text-[#1565C0] hover:underline font-medium">שכחתי סיסמא</Link>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-[#BDBDBD] dark:text-[#49454F] mt-6">
            מערכת ניהול מאובטחת · Amutech © 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
